/**
 * Modifié par : Landy
 * Date : 19/02/2026
 * 
 * Modifications du jour - Amélioration du système d'extraction des thématiques :
 * 
 * 1. NETTOYAGE DES CARACTÈRES SPÉCIAUX
 *    - Suppression des caractères «, *, " au début des titres pour une harmonie visuelle
 *    - Évite les thématiques comme "« formation" ou "* supervision"
 * 
 * 2. SUPPRESSION DES DIRECTIONS EN DÉBUT DE TITRE
 *    - Ignore les codes direction (DG, DIRA, DIRB, etc.) quand ils apparaissent au début
 *    - Permet d'extraire la véritable thématique au lieu de "DG suivi personnelle"
 * 
 * 3. SUPPRESSION DU MOT "MISSION" REDONDANT
 *    - Le mot "mission" au début est ignoré car redondant (on sait déjà que c'est une mission)
 *    - Évite la thématique générique "mission" dans l'affichage carte
 * 
 * 4. NETTOYAGE DES ORDINAUX POUR GÉNÉRALISATION
 *    - Suppression automatique des ordinaux (5e, 10e, 1er, 2eme, etc.)
 *    - Permet de regrouper "supervision 5e transfert" et "supervision 6e transfert" sous "supervision transfert"
 * 
 * 5. EXTRACTION INDIVIDUELLE DES THÉMATIQUES
 *    - Plus de concaténation avec "ET" qui créait des thématiques génériques
 *    - Chaque action (supervision, formation, appui, etc.) est extraite individuellement
 *    - Logique intelligente par verbes d'action : SUPERVISION, FORMATION, APPUI, SUIVI, SENSIBILISATION, etc.
 * 
 * 6. AJOUT DE STOP WORDS SUPPLÉMENTAIRES
 *    - Ajout du mot "PAR" dans les mots à ignorer pour éviter "supervision transfert par"
 *    - Nettoyage plus précis des thématiques
 * 
 * 7. SYNCHRONISATION FRONTEND/BACKEND
 *    - Toutes les modifications sont appliquées identiquement dans MissionGraphe.php (backend)
 *    - Garantit la cohérence entre l'affichage carte et les statistiques backend
 * 
 * Objectif final : Thématiques propres, généralisées et pertinentes pour l'affichage carte
 * Résultat attendu : Plus de "ET", "mission", "5e transfert par" dans les thématiques affichées
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { MapContainer, GeoJSON, useMap, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Grid, Paper, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableCell, TableRow, TableContainer, IconButton } from '@material-ui/core';
import { Map as MapIcon } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import MainContainer from "../../../components/MainContainer";
import { headerDataMission } from "../../../data/modules/mission-header";
import regionsData from "../../../data/regions.json";
import { regionsData as validRegions, isValidRegion, normalizeRegionName, regionColors } from "../../../data/regions";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import LoadingTexteBackdrop from "../../tools/LoadingTexteBackdrop";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    mapPaper: {
        position: 'relative',
        height: '82vh',
        width: '100%',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        backgroundColor: '#fff',
    },
    statsSidebar: {
        position: 'absolute',
        top: theme.spacing(2),
        right: theme.spacing(2),
        width: '300px',
        maxHeight: 'calc(100% - 32px)',
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.3)',
    },
    statsList: {
        overflowY: 'auto',
        marginTop: theme.spacing(1),
        paddingRight: theme.spacing(1),
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '10px',
        },
    },
    regionItem: {
        marginBottom: theme.spacing(1),
        padding: '8px 12px',
        borderRadius: '10px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'translateX(-5px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
    },
    title: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(3),
        color: '#053252ff',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    backButton: {
        marginBottom: theme.spacing(2),
        borderRadius: '4px',
        textTransform: 'none',
        fontWeight: 'bold',
    },
    tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: 'none',
        borderRadius: '4px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
        padding: '0 !important',
    }
}));

function MadagascarFit({ bounds }) {
    const map = useMap();

    useEffect(() => {
        if (!map || !bounds) return;

        map.fitBounds(bounds, { padding: [10, 10] });
    }, [map, bounds]);

    return null;
}

// Composant pour le dropdown des années
function YearDropdown({ year, setYear, options, id }) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const dropdownControl = L.control({ position: "topright" });

        dropdownControl.onAdd = () => {
            const div = L.DomUtil.create("div", "year-dropdown");
            div.style.padding = '10px';
            div.style.backgroundColor = 'white';
            div.style.borderRadius = '8px';
            div.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';

            const select = document.createElement("select");
            select.id = id;
            select.style.padding = "6px 10px";
            select.style.borderRadius = "4px";
            select.style.border = "1px solid #ddd";
            select.style.fontSize = "14px";

            options.forEach((y) => {
                const opt = document.createElement("option");
                opt.value = y;
                opt.text = y;
                if (y === year) opt.selected = true;
                select.appendChild(opt);
            });

            select.addEventListener("change", (e) => setYear(e.target.value));
            div.appendChild(select);
            L.DomEvent.disableClickPropagation(div);

            return div;
        };

        dropdownControl.addTo(map);

        return () => dropdownControl.remove();
    }, [map, options, setYear, id]);

    useEffect(() => {
        const select = document.getElementById(id);
        if (select) select.value = year;
    }, [year]);

    return null;
}

// Composant pour le titre de la carte
function MapTitle({ annee }) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const titleControl = L.control({ position: "topleft" });

        titleControl.onAdd = () => {
            const div = L.DomUtil.create("div", "map-title");
            div.style.backgroundColor = 'white';
            div.style.padding = '10px 15px';
            div.style.borderRadius = '8px';
            div.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
            div.innerHTML = `<h3 style="margin:0; color: #053252; font-size: 16px;">Répartition des missions - ${annee}</h3>`;
            return div;
        };

        titleControl.addTo(map);

        return () => {
            titleControl.remove();
        };
    }, [map, annee]);

    return null;
}

// Composant pour la légende
function MapLegend() {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const legendControl = L.control({ position: "bottomright" });

        legendControl.onAdd = () => {
            const div = L.DomUtil.create("div", "map-legend");
            div.style.backgroundColor = 'white';
            div.style.padding = '10px';
            div.style.borderRadius = '8px';
            div.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';

            div.innerHTML = `
                <h4 style="text-align: center; margin: 0 0 8px 0; color: #053252; font-size: 14px;">Légende des missions</h4>
                <ul style="list-style:none; padding:0; margin:0;">
                    <li style="display:flex; align-items:center; margin-bottom:6px;">
                        <span style="display:inline-block; width:16px; height:16px; background:#8B008B; margin-right:8px; border-radius:2px;"></span>
                        <span style="font-size: 12px;">15+ missions (Top 5)</span>
                    </li>
                    <li style="display:flex; align-items:center; margin-bottom:6px;">
                        <span style="display:inline-block; width:16px; height:16px; background:#FF6B6B; margin-right:8px; border-radius:2px;"></span>
                        <span style="font-size: 12px;">10-14 missions</span>
                    </li>
                    <li style="display:flex; align-items:center; margin-bottom:6px;">
                        <span style="display:inline-block; width:16px; height:16px; background:#FFA500; margin-right:8px; border-radius:2px;"></span>
                        <span style="font-size: 12px;">5-9 missions</span>
                    </li>
                    <li style="display:flex; align-items:center; margin-bottom:6px;">
                        <span style="display:inline-block; width:16px; height:16px; background:#90EE90; margin-right:8px; border-radius:2px;"></span>
                        <span style="font-size: 12px;">1-4 missions</span>
                    </li>
                    <li style="display:flex; align-items:center; margin-bottom:6px;">
                        <span style="display:inline-block; width:16px; height:16px; background:#E0E0E0; margin-right:8px; border-radius:2px;"></span>
                        <span style="font-size: 12px;">Aucune mission</span>
                    </li>
                </ul>
            `;
            return div;
        };

        legendControl.addTo(map);

        return () => legendControl.remove();
    }, [map]);

    return null;
}

export default function MissionMap() {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    // Données détaillées : une ligne par mission et par missionnaire
    const [missionData, setMissionData] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [communeMissionMap, setCommuneMissionMap] = useState({});
    const [regionDetails, setRegionDetails] = useState(null);
    const [openRegionDetails, setOpenRegionDetails] = useState(false);
    const detailsRef = useRef(null);
    const mapRef = useRef(null);
    const [topMissionnaireByRegion, setTopMissionnaireByRegion] = useState({});
    const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);

    const handleExportPDF = async () => {
        if (!mapRef.current) return;
        setOpenLoadingSimpleBackdrop(true);

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            let yPos = 15;

            // 0. Ajout du logo (comme dans l'autre export)
            try {
                const logoImg = await fetch(process.env.PUBLIC_URL + '/assets/images/logo.png');
                const logoBlob = await logoImg.blob();
                const logoBase64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(logoBlob);
                });
                pdf.addImage(logoBase64, 'PNG', 15, yPos, 50, 15);
                yPos += 25; // Espace après le logo
            } catch (error) {
                console.warn('Logo non chargé pour le PDF de la carte');
                yPos = 20; // Position de secours si le logo échoue
            }

            // 1. Titre
            pdf.setFontSize(20);
            pdf.setTextColor(5, 50, 82);
            pdf.setFont('helvetica', 'bold');
            pdf.text('REPARTITION DES MISSIONS', pageWidth / 2, yPos, { align: 'center' });

            pdf.setFontSize(10);
            pdf.setTextColor(120, 120, 120);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, yPos + 7, { align: 'center' });

            yPos += 13;

            // Ajout des filtres dans le PDF
            if (appliedFilters) {
                pdf.setDrawColor(230, 230, 230);
                pdf.setFillColor(248, 249, 250);
                pdf.roundedRect(15, yPos, pageWidth - 30, 22, 3, 3, 'FD');

                pdf.setFontSize(9);
                pdf.setTextColor(5, 50, 82);
                pdf.setFont('helvetica', 'bold');
                pdf.text('FILTRES APPLIQUÉS :', 20, yPos + 6);

                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(60, 60, 60);

                let filterText = [];
                if (appliedFilters.date_debut || appliedFilters.date_fin)
                    filterText.push(`Période: ${appliedFilters.date_debut || '?'} au ${appliedFilters.date_fin || '?'}`);
                if (appliedFilters.directionmiss)
                    filterText.push(`Dir. Missionnaire: ${appliedFilters.directionmiss.code}`);
                if (appliedFilters.region?.nom_region)
                    filterText.push(`Région: ${appliedFilters.region.nom_region}`);
                if (appliedFilters.directiondest)
                    filterText.push(`Dir. Destination: ${appliedFilters.directiondest.code}`);

                // Affichage sur deux lignes si trop long
                const line1 = filterText.slice(0, 2).join('  |  ');
                const line2 = filterText.slice(2).join('  |  ');

                pdf.text(line1, 20, yPos + 12);
                if (line2) pdf.text(line2, 20, yPos + 17);

                yPos += 30; // Espace après le bloc filtres
            } else {
                yPos += 10;
            }

            // 2. Capture optimisée
            const mapContainer = mapRef.current.querySelector('.leaflet-container');

            const canvas = await html2canvas(mapContainer, {
                useCORS: true,
                scale: 2,
                backgroundColor: '#B3E5FC',
                onclone: (clonedDoc) => {
                    const sidebar = clonedDoc.querySelector(`.${classes.statsSidebar.split(' ')[0]}`);
                    const zoom = clonedDoc.querySelector('.leaflet-control-zoom');
                    const attribution = clonedDoc.querySelector('.leaflet-control-attribution');
                    if (sidebar) sidebar.style.display = 'none';
                    if (zoom) zoom.style.display = 'none';
                    if (attribution) attribution.style.display = 'none';

                    const leafletContainer = clonedDoc.querySelector('.leaflet-container');
                    if (leafletContainer) {
                        leafletContainer.style.width = '1000px';
                        leafletContainer.style.height = '1200px';
                    }
                }
            });

            // 3. RECADRAGE POUR FOCUS SUR MADAGASCAR
            const croppedCanvas = document.createElement('canvas');
            const ctx = croppedCanvas.getContext('2d');

            // On redonne encore un peu d'espace à gauche (sx) pour un centrage parfait
            const sx = canvas.width * 0.25; // On descend à 25% (au lieu de 30%) pour libérer encore plus l'Ouest
            const sy = canvas.height * 0.02;
            const sWidth = canvas.width * 0.71; // On ajuste la largeur pour garder tout l'Est (0.25 + 0.71 = 0.96)
            const sHeight = canvas.height * 0.62; // On conserve la coupe en bas qui est validée

            croppedCanvas.width = sWidth;
            croppedCanvas.height = sHeight;

            ctx.drawImage(canvas, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight);

            const imgData = croppedCanvas.toDataURL('image/png');

            // On affiche l'île plus grande pour remplir l'espace (A4 = 210mm large x 297mm haut)
            const displayHeight = 205;
            const displayWidth = (sWidth * displayHeight) / sHeight;
            const xOffset = (pageWidth - displayWidth) / 2;

            pdf.addImage(imgData, 'PNG', xOffset, yPos, displayWidth, displayHeight);

            // --- PAGE 2 : REPARTITION PAR REGION AVEC COULEURS ---
            pdf.addPage();
            pdf.setFontSize(18);
            pdf.setTextColor(5, 50, 82);
            pdf.text('REPARTITION PAR REGION', pageWidth / 2, 20, { align: 'center' });

            let startY = 40;
            const startX = 25;
            const rowHeight = 10;
            const sortedStats = Object.entries(regionStats).sort((a, b) => a[0].localeCompare(b[0]));

            sortedStats.forEach(([name, count], index) => {
                if (startY > pageHeight - 20) {
                    pdf.addPage();
                    startY = 20;
                }
                const colorHex = regionColors[name] || '#3585CD';
                const r = parseInt(colorHex.slice(1, 3), 16);
                const g = parseInt(colorHex.slice(3, 5), 16);
                const b = parseInt(colorHex.slice(5, 7), 16);
                pdf.setFillColor(r, g, b);
                pdf.roundedRect(startX, startY - 4, 6, 6, 1, 1, 'F');
                pdf.setTextColor(60, 60, 60);
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.text(name.toUpperCase(), startX + 10, startY);
                pdf.setFillColor(240, 240, 240);
                pdf.roundedRect(pageWidth - 45, startY - 5, 15, 7, 2, 2, 'F');
                pdf.setTextColor(5, 50, 82);
                pdf.text(count.toString(), pageWidth - 37.5, startY, { align: 'center' });
                pdf.setDrawColor(240, 240, 240);
                pdf.line(startX, startY + 3, pageWidth - 25, startY + 3);
                startY += rowHeight;
            });

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.setTextColor(150, 150, 150);
                pdf.text(`Document PGI - Page ${i}/${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }

            pdf.save(`statistiques_missions_par_regions${new Date().toISOString().split('T')[0]}.pdf`);

        } catch (error) {
            console.error('Erreur export PDF:', error);
        } finally {
            setOpenLoadingSimpleBackdrop(false);
        }
    };

    const handleExportExcel = async () => {
        setOpenLoadingSimpleBackdrop(true);
        try {
            const workbook = new ExcelJS.Workbook();

            // --- FEUILLE 1 : REPARTITION GLOBALE ---
            const worksheet = workbook.addWorksheet('Répartition par Région');

            // 0. Ajout du Logo
            try {
                const response = await fetch(process.env.PUBLIC_URL + '/assets/images/logo.png');
                const imageBuffer = await response.arrayBuffer();
                const logoId = workbook.addImage({
                    buffer: imageBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.1, row: 0.1 },
                    ext: { width: 180, height: 50 }
                });
            } catch (imgError) {
                console.warn('Logo non trouvé pour Sheet 1');
            }

            // Style pour les en-têtes
            const headerStyle = {
                font: { bold: true, color: { argb: 'FFFFFFFF' } },
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF053252' } },
                alignment: { horizontal: 'center', vertical: 'middle' },
                border: {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                }
            };

            // Titre du document (Décalé pour le logo)
            worksheet.mergeCells('A4:C4');
            const titleRow = worksheet.getRow(4);
            titleRow.getCell(1).value = 'RÉPARTITION GLOBALE DES MISSIONS';
            titleRow.getCell(1).font = { size: 14, bold: true, color: { argb: 'FF053252' } };
            titleRow.alignment = { vertical: 'middle', horizontal: 'center' };

            worksheet.addRow([]); // Ligne vide

            // Ajout des filtres dans Excel
            if (appliedFilters) {
                worksheet.addRow(['Filtres appliqués :']);
                const filterRow = worksheet.lastRow;
                filterRow.getCell(1).font = { bold: true, italic: true, size: 10 };

                if (appliedFilters.date_debut || appliedFilters.date_fin)
                    worksheet.addRow(['Période :', `${appliedFilters.date_debut || '?'} au ${appliedFilters.date_fin || '?'}`]);
                if (appliedFilters.directionmiss)
                    worksheet.addRow(['Dir. Missionnaire :', appliedFilters.directionmiss.code]);
                if (appliedFilters.region?.nom_region)
                    worksheet.addRow(['Région cible :', appliedFilters.region.nom_region]);
                if (appliedFilters.directiondest)
                    worksheet.addRow(['Dir. Destination :', appliedFilters.directiondest.code]);

                worksheet.addRow([]); // Ligne de séparation
            }

            worksheet.addRow([]); // Ligne vide

            // En-têtes du tableau
            const headersSummary = ['Région', 'Nombre de missions', 'Niveau d\'activité'];
            const headerRowSummary = worksheet.addRow(headersSummary);
            headerRowSummary.eachCell((cell) => {
                cell.style = headerStyle;
            });

            // Données synthèse avec calcul du niveau d'activité
            Object.entries(regionStats).sort((a, b) => a[0].localeCompare(b[0])).forEach(([name, count]) => {
                /* 
              CRITÈRES DES NIVEAUX D'ACTIVITÉ :
              - Très Élevé : 15 missions ou plus
              - Élevé      : 10 à 14 missions
              - Moyen      : 5 à 9 missions
              - Faible     : 1 à 4 missions
              - Aucune     : 0 mission
           */
                let activite = 'Faible';
                if (count >= 15) activite = 'Très Élevé';
                else if (count >= 10) activite = 'Élevé';
                else if (count >= 5) activite = 'Moyen';
                else if (count === 0) activite = 'Aucune';

                const row = worksheet.addRow([name.toUpperCase(), count, activite]);
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });

            worksheet.getColumn(1).width = 30;
            worksheet.getColumn(2).width = 25;
            worksheet.getColumn(3).width = 20;

            // --- FEUILLE 2 : DETAILS APPROFONDIS ---
            const detailSheet = workbook.addWorksheet('Détails par Région');

            // 0. Ajout du Logo sur Sheet 2
            try {
                const response = await fetch(process.env.PUBLIC_URL + '/assets/images/logo.png');
                const imageBuffer = await response.arrayBuffer();
                const logoId = workbook.addImage({
                    buffer: imageBuffer,
                    extension: 'png',
                });
                detailSheet.addImage(logoId, {
                    tl: { col: 0.1, row: 0.1 },
                    ext: { width: 180, height: 50 }
                });
            } catch (imgError) {
                console.warn('Logo non trouvé pour Sheet 2');
            }

            detailSheet.mergeCells('A4:J4');
            const detailTitleRow = detailSheet.getRow(4);
            detailTitleRow.getCell(1).value = 'STATISTIQUES DÉTAILLÉES PAR RÉGION';
            detailTitleRow.getCell(1).font = { size: 14, bold: true, color: { argb: 'FF053252' } };
            detailTitleRow.alignment = { vertical: 'middle', horizontal: 'center' };

            detailSheet.addRow([]); // Ligne vide

            // Ajout des filtres dans la feuille de détails
            if (appliedFilters) {
                detailSheet.addRow(['Filtres appliqués :']);
                const filterRow = detailSheet.lastRow;
                filterRow.getCell(1).font = { bold: true, italic: true, size: 10 };

                if (appliedFilters.date_debut || appliedFilters.date_fin)
                    detailSheet.addRow(['Période :', `${appliedFilters.date_debut || '?'} au ${appliedFilters.date_fin || '?'}`]);
                if (appliedFilters.directionmiss)
                    detailSheet.addRow(['Dir. Missionnaire :', appliedFilters.directionmiss.code]);
                if (appliedFilters.region?.nom_region)
                    detailSheet.addRow(['Région cible :', appliedFilters.region.nom_region]);
                if (appliedFilters.directiondest)
                    detailSheet.addRow(['Dir. Destination :', appliedFilters.directiondest.code]);

                detailSheet.addRow([]); // Ligne de séparation
            }

            detailSheet.addRow([]);

            const headersDetail = [
                'Région',
                'Total Missions',
                'Missionnaire le plus actif',
                'Missions du missionnaire',
                'Sa Direction',
                'Son Service',
                'Thématique principale',
                'Direction la plus active',
                'District le plus visité',
                'Commune la plus visitée'
            ];

            const headerRowDetail = detailSheet.addRow(headersDetail);
            headerRowDetail.eachCell((cell) => {
                cell.style = headerStyle;
            });

            // Utiliser les statistiques réelles pour remplir les détails (triés par importance)
            Object.entries(regionStats).sort((a, b) => a[0].localeCompare(b[0])).forEach(([regionName, count]) => {
                const details = buildRegionDetails(regionName);

                // On n'ajoute que les régions qui ont au moins une mission
                if (details.missionsCount > 0) {
                    const row = detailSheet.addRow([
                        regionName.toUpperCase(),
                        details.missionsCount,
                        details.topMissionnaire ? details.topMissionnaire[0] : 'Non précisé',
                        details.topMissionnaire ? details.topMissionnaire[1] : 0,
                        details.topMissionnaireDirection ? details.topMissionnaireDirection[0] : 'Non précisé',
                        details.topMissionnaireService ? details.topMissionnaireService[0] : 'Non précisé',
                        details.topThematique ? details.topThematique[0].toUpperCase() : 'Non précisé',
                        details.topDirection ? details.topDirection[0] : 'Non précisé',
                        details.topDistrict ? details.topDistrict[0] : 'Non précisé',
                        details.topCommune ? details.topCommune[0] : 'Non précisé'
                    ]);

                    row.eachCell((cell) => {
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                        cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    });
                }
            });

            // Ajustement automatique des colonnes pour les détails
            detailSheet.getColumn(1).width = 25; // Region
            detailSheet.getColumn(2).width = 15; // Total
            detailSheet.getColumn(3).width = 30; // Top User
            detailSheet.getColumn(4).width = 20; // Count User
            detailSheet.getColumn(5).width = 25; // SA Direction
            detailSheet.getColumn(6).width = 25; // SON Service
            detailSheet.getColumn(7).width = 30; // Thematique
            detailSheet.getColumn(8).width = 25; // Top Direction
            detailSheet.getColumn(9).width = 25; // Top District
            detailSheet.getColumn(10).width = 25; // Top Commune

            // Génération du fichier
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `statistiques_missions_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
        } catch (error) {
            console.error('Erreur export Excel:', error);
        } finally {
            setOpenLoadingSimpleBackdrop(false);
        }
    };


    useEffect(() => {
        // On essaye d'abord de récupérer les données via le state de navigation (préférable pour les gros volumes)
        const navigationState = location.state || {};

        const data = navigationState.mapMissionData || (localStorage.getItem('MAP_MISSION_DATA') ? JSON.parse(localStorage.getItem('MAP_MISSION_DATA')) : null);
        const rawTop = navigationState.mapMissionTopByRegion || (localStorage.getItem('MAP_MISSION_TOP_BY_REGION') ? JSON.parse(localStorage.getItem('MAP_MISSION_TOP_BY_REGION')) : null);
        const filters = navigationState.mapFilterState || (localStorage.getItem('MAP_FILTER_STATE') ? JSON.parse(localStorage.getItem('MAP_FILTER_STATE')) : null);

        if (data) {
            setMissionData(data);
        } else {
            console.log('Aucune donnée trouvée (ni state, ni localStorage)');
        }

        if (rawTop) {
            const normalizedTop = {};
            Object.entries(rawTop).forEach(([name, info]) => {
                const norm = normalizeRegionName(name);
                if (norm) {
                    normalizedTop[norm] = info;
                }
            });
            setTopMissionnaireByRegion(normalizedTop);
        }

        if (filters) {
            setAppliedFilters(filters);
        }
    }, [location]);

    // Fonction pour obtenir la couleur d'une région
    const getRegionColor = (regionName) => {
        const normalizedName = normalizeRegionName(regionName);
        return normalizedName && regionColors[normalizedName] ? regionColors[normalizedName] : '#E0E0E0';
    };

    const getTopEntries = (countMap, limit = 1) => {
        const entries = Object.entries(countMap);
        if (entries.length === 0) return [];
        const validEntries = entries.filter((e) => e[0] && e[0] !== 'Non spécifié' && e[0] !== 'non spécifié' && e[0] !== 'non défini' && e[0] !== 'ND' && e[0] !== 'null');
        if (validEntries.length === 0) return [];
        return validEntries.sort((a, b) => b[1] - a[1]).slice(0, limit);
    };

    const buildRegionDetails = (regionName) => {
        const regionMissions = missionData.filter((m) => normalizeRegionName(m.nom_region) === regionName);

        // Même logique que le backend : regrouper par user_id, dédoublonner par user_id + mission_planning_id
        const missionnaires = {};        // Clé: user_id, Valeur: { name, direction, service, count, seenMissions }
        const thematiqueStats = {};
        const directionStats = {};
        const districtStats = {};
        const communeStats = {};
        const seenMissionsForCount = new Set();

        regionMissions.forEach((m) => {
            const userId = m.user_id || m.nom || 'unknown';
            const missionId = m.mission_planning_id;

            // Initialiser le missionnaire si pas encore vu
            if (!missionnaires[userId]) {
                missionnaires[userId] = {
                    name: (m.nom || 'Non spécifié').trim(),
                    direction: (m.directionpers || 'Non défini').trim(),
                    service: (m.service_code || 'Non défini').trim(),
                    count: 0,
                    seenMissions: new Set()
                };
            }

            // Pour le top missionnaire, on dédoublonne par mission_planning_id s'il existe
            if (missionId) {
                if (!missionnaires[userId].seenMissions.has(missionId)) {
                    missionnaires[userId].count++;
                    missionnaires[userId].seenMissions.add(missionId);
                }
            } else {
                // Si pas d'ID, on compte chaque ligne pour la personne
                missionnaires[userId].count++;
            }

            // Pour les stats globales (thématiques, directions, total région)
            // On applique la même règle : dédoublonnage seulement si ID présent
            let shouldCountGlobally = false;
            if (missionId) {
                if (!seenMissionsForCount.has(missionId)) {
                    seenMissionsForCount.add(missionId);
                    shouldCountGlobally = true;
                }
            } else {
                // Sans ID, on compte chaque ligne comme une "mission" distincte
                shouldCountGlobally = true;
            }

            if (shouldCountGlobally) {
                const thematique = extractThematique(m);
                thematiqueStats[thematique] = (thematiqueStats[thematique] || 0) + 1;

                const direction = (m.directionpers || 'Non défini').trim();
                directionStats[direction] = (directionStats[direction] || 0) + 1;

                const district = (m.nom_district || '').toString().trim();
                if (district) districtStats[district] = (districtStats[district] || 0) + 1;

                const commune = (m.lieu_destination || '').toString().trim();
                if (commune) communeStats[commune] = (communeStats[commune] || 0) + 1;
            }
        });

        // Calcul du total final pour le retour
        let finalRegionCount = 0;
        const regionSeen = new Set();
        regionMissions.forEach(m => {
            if (m.mission_planning_id) {
                if (!regionSeen.has(m.mission_planning_id)) {
                    regionSeen.add(m.mission_planning_id);
                    finalRegionCount++;
                }
            } else {
                finalRegionCount++;
            }
        });

        const backendTop = topMissionnaireByRegion[regionName];

        let topMissionnairesList = [];
        if (backendTop) {
            topMissionnairesList = [{
                name: backendTop.name,
                count: backendTop.count,
                direction: backendTop.direction || 'Non défini',
                service: backendTop.service || 'Non défini'
            }];
        } else {
            const missionnaireEntries = Object.values(missionnaires);
            missionnaireEntries.sort((a, b) => b.count - a.count);
            topMissionnairesList = missionnaireEntries.slice(0, 3).map(m => ({
                name: m.name,
                count: m.count,
                direction: m.direction,
                service: m.service
            }));
        }

        const top3Thematiques = getTopEntries(thematiqueStats, 3);
        const top3Districts = getTopEntries(districtStats, 3);
        const top3Communes = getTopEntries(communeStats, 3);
        const top3Directions = getTopEntries(directionStats, 3);

        // Préparation des versions simples pour l'export Excel et les popups
        const topMissionnaire = topMissionnairesList.length > 0 ? [topMissionnairesList[0].name, topMissionnairesList[0].count] : null;
        const topMissionnaireDirection = topMissionnairesList.length > 0 ? [topMissionnairesList[0].direction, topMissionnairesList[0].count] : null;
        const topMissionnaireService = topMissionnairesList.length > 0 ? [topMissionnairesList[0].service, topMissionnairesList[0].count] : null;

        // Créer la liste des missions uniques dédoublonnées pour le tableau
        const uniqueMissions = [];
        const seenMissionIds = new Set();

        regionMissions.forEach(m => {
            if (m.mission_planning_id) {
                if (!seenMissionIds.has(m.mission_planning_id)) {
                    seenMissionIds.add(m.mission_planning_id);
                    uniqueMissions.push(m);
                }
            } else {
                // Si pas d'ID, on ajoute quand même la mission
                uniqueMissions.push(m);
            }
        });

        return {
            regionName,
            missionsCount: finalRegionCount,
            topMissionnairesList,
            top3Thematiques,
            top3Districts,
            top3Communes,
            top3Directions,
            topMissionnaire,
            topMissionnaireDirection,
            topMissionnaireService,
            topThematique: top3Thematiques[0] || null,
            topDirection: top3Directions[0] || null,
            topDistrict: top3Districts[0] || null,
            topCommune: top3Communes[0] || null,
            missions: uniqueMissions
        };
    };

    const openRegionPanel = (regionName) => {
        setRegionDetails(buildRegionDetails(regionName));
        setTimeout(() => {
            detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    };

    // Fonction pour extraire la thématique courte (utilise les titres nettoyés par le backend)
    const extractThematique = (mission) => {
        // Si le backend a fourni un titre nettoyé, on l'utilise directement
        if (mission.mission_title_clean) {
            return mission.mission_title_clean;
        }

        // Sinon, on utilise le titre original avec la logique de nettoyage locale
        const rawTitle = mission.mission_title;
        if (!rawTitle) return 'Non spécifié';

        // 1. Mise en majuscules et nettoyage de base
        let title = rawTitle.trim().toUpperCase();
        title = title.split(/[,:;]| N[°º]/)[0];

        // Nettoyage des caractères spéciaux indésirables au début
        title = title.replace(/^[«*\"\s]+/, '').trim();

        // 2. Suppression des directions en début de titre
        const directionsToSkip = ['DG', 'DIRA', 'DIRB', 'DIRT', 'DIRP', 'DIRK', 'DIRF', 'DIRU', 'ARH', 'ARM', 'ARS', 'DRB', 'DRT', 'DRP', 'DRK', 'DRF', 'DRU', 'DR'];
        let words = title.split(' ');

        // Si le premier mot est une direction, on le saute
        if (words.length > 0 && directionsToSkip.includes(words[0])) {
            words.shift(); // On retire la direction du début
            title = words.join(' '); // On reconstruit le titre sans la direction
        }

        // 2.5. Suppression du mot "MISSION" au début du titre (redondant)
        words = title.split(' ');
        if (words.length > 0 && words[0] === 'MISSION') {
            words.shift(); // On retire "MISSION" du début
            title = words.join(' '); // On reconstruit le titre sans "MISSION"
        }

        // 2.6. Nettoyage des ordinaux et chiffres pour généraliser les thématiques
        title = title.replace(/\b\d+[eè]r?[eè]me?\b|\b\d+[eè]r?\b/gi, '');
        title = title.replace(/\b\d+[eè][rme]*\b/gi, '');
        title = title.replace(/\b\d+\b/g, ''); // Supprime les chiffres seuls
        title = title.replace(/\s+/g, ' ').trim(); // Nettoyage des espaces multiples

        // 3. Logique intelligente par Verbe d'Action - Extraction de thématiques individuelles
        words = title.split(' ');
        const actionVerbs = ['SUPERVISION', 'FORMATION', 'MISSION', 'COLLECTE', 'EVALUATION', 'APPUI', 'MISE', 'REUNION', 'ATELIER', 'PRISE', 'SUIVI', 'SENSIBILISATION', 'ACCOMPAGNEMENT', 'CONTROLE', 'VERIFICATION', 'ANALYSE', 'MONITORING', 'ENQUETE', 'RECOUVREMENT', 'VISITE', 'AUDIT', 'INSPECTION', 'COMMUNICATION', 'RAPPORT', 'PROJET'];
        const stopWords = ['DU', 'DES', 'AUX', 'LES', 'SUR', 'DE', 'LE', 'LA', 'D\'', 'POUR', 'AVEC', 'DANS', 'AU', 'PAR', 'DERNIERS', 'DERNIERE', 'DERNIERES', 'EN', 'A', 'À', 'UN', 'UNE'];
        const timeWords = ['JOUR', 'JOURS', 'MOIS', 'AN', 'ANS', 'ANNEE', 'ANNEES', 'SEMAINE', 'SEMAINES'];

        const thematiquesExtraites = [];
        let currentSegment = [];

        for (let i = 0; i < words.length; i++) {
            const w = words[i];

            // Cas spécial : si on trouve "ET" ou "&", on termine le segment actuel et on continue avec le mot suivant
            if (w === 'ET' || w === '&') {
                if (currentSegment.length > 0) {
                    thematiquesExtraites.push(currentSegment.join(' '));
                    currentSegment = [];
                }
                continue; // On saute "ET" ou "&" mais on garde le mot suivant
            }

            // On ignore les petits mots de liaison sauf "ET" et "&" qui sont gérés ci-dessus
            if (stopWords.includes(w)) continue;


            // Si on trouve un verbe d'action important, on termine le segment précédent (s'il y en a un) et on commence un nouveau
            if (actionVerbs.includes(w)) {
                if (currentSegment.length > 0) {
                    thematiquesExtraites.push(currentSegment.join(' '));
                    currentSegment = [];
                }

                const segment = [w];
                let nextWordsCount = 0;
                let j = i + 1;
                for (; j < words.length && nextWordsCount < 2; j++) {
                    const nextWord = words[j];
                    const nextWordNorm = nextWord.replace('À', 'A');
                    // Exception : On garde 'EN', 'A', 'À' s'ils suivent 'MISE' ou 'PRISE'
                    const isLinkWord = nextWordNorm === 'EN' || nextWordNorm === 'A';
                    const lastWord = segment[segment.length - 1];
                    if (isLinkWord && (lastWord === 'MISE' || lastWord === 'PRISE')) {
                        segment.push(nextWord);
                        continue;
                    }

                    // Exception : On capture 'JOUR' si on a déjà 'MISE'
                    const isJour = nextWordNorm === 'JOUR' || nextWordNorm === 'JOURS';
                    if (isJour && segment.includes('MISE')) {
                        segment.push(nextWord);
                        nextWordsCount = 2;
                        break;
                    }

                    if (nextWord === 'PROGRAMME') {
                        segment.push('PROGRAMME');
                        for (let k = j + 1; k < words.length; k++) {
                            const candidate = words[k];
                            if (stopWords.includes(candidate)) continue;
                            if (timeWords.includes(candidate)) continue;
                            if (!isNaN(Number(candidate))) continue;
                            segment.push(candidate);
                            j = k + 1;
                            break;
                        }
                        nextWordsCount = 2;
                        break;
                    }

                    if (stopWords.includes(nextWord)) continue;
                    if (timeWords.includes(nextWordNorm)) {
                        continue;
                    }
                    if (!isNaN(Number(nextWord))) continue;
                    segment.push(nextWord);
                    nextWordsCount++;
                }

                thematiquesExtraites.push(segment.join(' '));
                i = j - 1; // On saute les mots parcourus dans la boucle
                continue;
            }

            // Si on trouve "PROGRAMME", on le garde et on prend le mot d'après
            if (w === 'PROGRAMME') {
                if (currentSegment.length > 0) {
                    thematiquesExtraites.push(currentSegment.join(' '));
                }
                currentSegment = ['PROGRAMME'];
                if (words[i + 1]) {
                    currentSegment.push(words[i + 1]);
                }
                continue;
            }

            // On ajoute le mot au segment actuel si on a déjà un verbe d'action
            if (currentSegment.length > 0) {
                currentSegment.push(w);

                // On limite à 3 mots par segment pour éviter les thématiques trop longues
                if (currentSegment.length >= 3) {
                    thematiquesExtraites.push(currentSegment.join(' '));
                    currentSegment = [];
                }
            }
        }

        // On ajoute le dernier segment s'il reste des mots
        if (currentSegment.length > 0) {
            thematiquesExtraites.push(currentSegment.join(' '));
        }

        // On filtre pour ne garder que les thématiques d'au moins 2 mots
        const finalResults = thematiquesExtraites.filter(t => t.trim().split(' ').length >= 2);

        if (finalResults.length === 0) {
            // Si tout était trop court, on prend les 2 ou 3 premiers mots significatifs du titre original
            const filteredWords = words.filter(w => !stopWords.includes(w) && !timeWords.includes(w) && isNaN(Number(w)));
            return filteredWords.slice(0, 3).join(' ').toLowerCase() || 'non spécifié';
        }

        return finalResults.join(' & ').toLowerCase();
    };

    // Fonction pour styliser chaque région
    const styleRegion = (feature) => {
        const regionName = feature.properties.shapeName?.trim();

        return {
            fillColor: getRegionColor(regionName),
            weight: 2, // Frontières des régions bien visibles
            opacity: 1,
            color: 'white',
            dashArray: '',
            fillOpacity: 0.7
        };
    };

    // Fonction pour gérer chaque feature (région)
    const onEachFeature = (feature, layer) => {
        const regionName = normalizeRegionName(feature.properties.shapeName);

        // Ne traiter que les régions valides
        if (!regionName || !isValidRegion(regionName)) {
            return;
        }

        // Utiliser buildRegionDetails pour avoir les mêmes statistiques partout
        const details = buildRegionDetails(regionName);
        const nbMissions = details.missionsCount;
        const topMissionnaire = details.topMissionnaire;
        const topMissionnaireDirection = details.topMissionnaireDirection;
        const topMissionnaireService = details.topMissionnaireService;
        const topThematique = details.topThematique;

        // Créer le contenu du popup enrichi
        const popupContent = `
            <div style="min-width: 200px;">
                <h4 style="margin: 0 0 12px 0; color: #053252; border-bottom: 2px solid #053252; padding-bottom: 5px;">${regionName}</h4>
                <p style="margin: 6px 0; color: #053252;">
                    <strong>Nombre de missions:</strong> ${nbMissions}
                </p>
                ${topMissionnaire ? `
                    <p style="margin: 6px 0; color: #053252;">
                        <strong>Missionnaire le plus actif:</strong><br/>
                        <span style="font-style: italic; color: #666;">${topMissionnaire[0]}${topMissionnaireDirection || topMissionnaireService ? ` (${[topMissionnaireDirection?.[0], topMissionnaireService?.[0]].filter(Boolean).join(' / ')})` : ''}</span> (${topMissionnaire[1]} missions)
                    </p>
                ` : ''}
                ${topThematique ? `
                    <p style="margin: 6px 0; color: #053252;">
                        <strong>Thématique la plus courante:</strong><br/>
                        <span style="font-style: italic; color: #666; text-transform: capitalize;">${topThematique[0]}</span> (${topThematique[1]} fois)
                    </p>
                ` : ''}
            </div>
        `;

        layer.bindPopup(popupContent);

        // Afficher en permanence le nom de la région et le nombre de missions
        layer.bindTooltip(
            `<div class="region-permanent-label">
                <div class="region-name-text">${regionName}</div>
                <div class="region-count-text">${nbMissions}</div>
            </div>`,
            {
                permanent: true,
                direction: 'center',
                offset: [0, 0],
                className: 'region-label-tooltip region-label-styled'
            }
        );

        // Survol : Highlight seulement l'aspect visuel de la région
        layer.on('mouseover', (e) => {
            layer.setStyle({
                weight: 3,
                fillOpacity: 0.85
            });
        });

        layer.on('mouseout', (e) => {
            layer.setStyle(styleRegion(feature));
        });

        // Clic : ouvrir le panneau de détails
        layer.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            layer.setStyle(styleRegion(feature));
            openRegionPanel(regionName);
        });
    };

    // Calculer les statistiques par région pour l'affichage en dessous (toutes les régions valides)
    // Comptage par région en se basant sur des missions UNIQUES
    // (même logique que le backend : une mission ne doit être comptée qu'une fois)
    const regionStats = {};
    const regionSeenIds = {}; // Pour stocker les Sets par région

    missionData.forEach((curr) => {
        const regionName = normalizeRegionName(curr.nom_region);
        if (!regionName || !isValidRegion(regionName)) return;

        const missionId = curr.mission_planning_id;

        if (missionId) {
            if (!regionSeenIds[regionName]) regionSeenIds[regionName] = new Set();
            if (!regionSeenIds[regionName].has(missionId)) {
                regionSeenIds[regionName].add(missionId);
                regionStats[regionName] = (regionStats[regionName] || 0) + 1;
            }
        } else {
            // Sans ID, on incrémente directement
            regionStats[regionName] = (regionStats[regionName] || 0) + 1;
        }
    });

    // Fenêtre de vue pour voir l'ensemble de Madagascar
    const madagascarBounds = L.latLngBounds(
        L.latLng(-26.0, 42.0),
        L.latLng(-11.0, 52.0)
    );

    // Ajouter toutes les régions valides avec 0 mission si elles ne sont pas présentes
    validRegions.forEach(region => {
        if (!regionStats[region.nom_region]) {
            regionStats[region.nom_region] = 0;
        }
    });

    return (
        <MainContainer {...headerDataMission}>
            <LoadingTexteBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
            <Box className={classes.root}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        className={classes.backButton}
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate("/mission_graphe", {
                            state: location.state // On renvoie le même state pour que le dashboard puisse se restaurer
                        })}
                    >
                        Retour aux statistiques
                    </Button>

                    <Box display="flex" gap={2}>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: '#f44336', color: 'white', fontWeight: 'bold' }}
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPDF}
                        >
                            PDF
                        </Button>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 'bold' }}
                            startIcon={<TableViewIcon />}
                            onClick={handleExportExcel}
                        >
                            Excel
                        </Button>
                    </Box>
                </Box>

                <Typography variant="h4" className={classes.title}>
                    <MapIcon fontSize="large" color="primary" />
                    Répartition Géographique des Missions
                </Typography>

                {/* Bandeau des filtres appliqués */}
                {appliedFilters && (
                    <Box mb={3} p={2} style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}>
                        <Typography variant="caption" style={{
                            fontWeight: 'bold',
                            color: '#053252',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}>
                            Filtres appliqués :
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {(appliedFilters.date_debut || appliedFilters.date_fin) && (
                                <Chip
                                    size="small"
                                    label={`Période: ${appliedFilters.date_debut || '?'} au ${appliedFilters.date_fin || '?'}`}
                                    style={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
                                />
                            )}
                            {appliedFilters.directionmiss && (
                                <Chip
                                    size="small"
                                    label={`Dir. Missionnaire: ${appliedFilters.directionmiss.code}`}
                                    style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}
                                />
                            )}
                            {appliedFilters.servicemiss && (
                                <Chip
                                    size="small"
                                    label={`Serv. Missionnaire: ${appliedFilters.servicemiss.code}`}
                                    variant="outlined"
                                />
                            )}
                            {appliedFilters.region && appliedFilters.region.nom_region && (
                                <Chip
                                    size="small"
                                    label={`Région: ${appliedFilters.region.nom_region}`}
                                    style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                                />
                            )}
                            {appliedFilters.directiondest && (
                                <Chip
                                    size="small"
                                    label={`Dir. Destination: ${appliedFilters.directiondest.code}`}
                                    style={{ backgroundColor: '#fff3e0', color: '#ef6c00', fontWeight: 600 }}
                                />
                            )}
                            {appliedFilters.group && (
                                <Chip
                                    size="small"
                                    label={`Axe Y: ${appliedFilters.group.label}`}
                                    variant="outlined"
                                    style={{ borderStyle: 'dashed' }}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                <Paper className={classes.mapPaper} ref={mapRef}>
                    <MapContainer
                        center={[-18.7669, 46.8691]}
                        zoom={5.8}
                        maxBounds={madagascarBounds}
                        maxBoundsViscosity={1.0}
                        style={{ height: '100%', width: '100%', backgroundColor: '#B3E5FC' }}
                        zoomControl={true}
                        preferCanvas={true}
                    >
                        <GeoJSON
                            data={regionsData}
                            style={styleRegion}
                            onEachFeature={onEachFeature}
                        />

                        <ZoomControl position="bottomleft" />
                    </MapContainer>

                    {/* Sidebar de répartition intégrée à la carte */}
                    <Box className={classes.statsSidebar}>
                        <Typography variant="subtitle1" style={{ color: '#053252', fontWeight: 'bold', marginBottom: 8 }}>
                            Répartition par région
                        </Typography>
                        <Box className={classes.statsList}>
                            {Object.entries(regionStats).sort((a, b) => a[0].localeCompare(b[0])).map(([name, count]) => {
                                const regionColor = getRegionColor(name);
                                return (
                                    <Box
                                        key={name}
                                        className={classes.regionItem}
                                        style={{
                                            backgroundColor: regionColor + '15',
                                            borderLeft: `4px solid ${regionColor}`,
                                        }}
                                        onClick={() => openRegionPanel(name)}
                                    >
                                        <Typography variant="caption" style={{ color: '#053252', fontWeight: 600 }}>
                                            {name}
                                        </Typography>
                                        <Chip
                                            label={count}
                                            size="small"
                                            style={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold',
                                                backgroundColor: regionColor,
                                                color: 'white'
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Paper>

                <Box mt={2}>
                    {regionDetails && (
                        <Box mt={1} ref={detailsRef}>
                            <Paper variant="outlined" style={{ padding: 16, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.9)' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="h6" style={{ color: '#053252', fontWeight: 'bold' }}>
                                        Détails - {regionDetails.regionName}
                                    </Typography>
                                    <Box>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            onClick={() => setOpenRegionDetails(true)}
                                        >
                                            Plus d'infos
                                        </Button>
                                        <IconButton
                                            onClick={() => setRegionDetails(null)}
                                            style={{ color: 'red' }}
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Table size="small" style={{ marginTop: 12 }}>
                                    <TableHead>
                                        <TableRow style={{ backgroundColor: 'rgba(5, 50, 82, 0.05)' }}>
                                            <TableCell style={{ color: '#053252', fontWeight: 'bold' }}>Libellé</TableCell>
                                            <TableCell style={{ color: '#053252', fontWeight: 'bold' }}>Valeur</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow hover>
                                            <TableCell>Nb missions Total</TableCell>
                                            <TableCell><strong>{regionDetails.missionsCount}</strong></TableCell>
                                        </TableRow>
                                        <TableRow hover>
                                            <TableCell>Thématique (Objet de missions)</TableCell>
                                            <TableCell>
                                                {regionDetails.top3Thematiques.length > 0 ? (
                                                    regionDetails.top3Thematiques.map((t, i) => (
                                                        <Box key={i} mb={0.5} style={{ textTransform: 'capitalize' }}>
                                                            <strong>{t[0]}</strong> ({t[1]} fois)
                                                        </Box>
                                                    ))
                                                ) : 'Non spécifié'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow hover>
                                            <TableCell>Missionnaire le plus actif</TableCell>
                                            <TableCell>
                                                {regionDetails.topMissionnairesList.length > 0 ? (
                                                    regionDetails.topMissionnairesList.map((m, i) => (
                                                        <Box key={i} mb={0.5}>
                                                            <strong>{m.name}</strong> ({m.count} missions)
                                                        </Box>
                                                    ))
                                                ) : 'Non spécifié'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow hover>
                                            <TableCell>Sa Direction</TableCell>
                                            <TableCell>
                                                <strong>{regionDetails.topMissionnairesList[0]?.direction || 'Non spécifié'}</strong>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow hover>
                                            <TableCell>Son Service</TableCell>
                                            <TableCell>
                                                <strong>{regionDetails.topMissionnairesList[0]?.service || 'Non spécifié'}</strong>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow hover>
                                            <TableCell>District le plus visité</TableCell>
                                            <TableCell>
                                                {regionDetails.top3Districts.length > 0 ? (
                                                    regionDetails.top3Districts.map((d, i) => (
                                                        <Box key={i} mb={0.5}>
                                                            <strong>{d[0]}</strong> ({d[1]})
                                                        </Box>
                                                    ))
                                                ) : 'Non spécifié'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow hover>
                                            <TableCell>Commune la plus visitée</TableCell>
                                            <TableCell>
                                                {regionDetails.top3Communes.length > 0 ? (
                                                    regionDetails.top3Communes.map((c, i) => (
                                                        <Box key={i} mb={0.5}>
                                                            <strong>{c[0]}</strong> ({c[1]})
                                                        </Box>
                                                    ))
                                                ) : 'Non spécifié'}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}
                </Box>

                <Dialog
                    open={openRegionDetails}
                    onClose={() => setOpenRegionDetails(false)}
                    fullWidth
                    maxWidth="md"
                >
                    <DialogTitle style={{ color: '#053252' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                <span style={{ fontWeight: 'bold' }}>Toutes les missions - {regionDetails?.regionName || ''}</span>
                                <Typography variant="body2" style={{ color: '#666', fontSize: '0.875rem' }}>
                                    Total: <strong>{regionDetails?.missions?.length || 0}</strong> missions
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={() => setOpenRegionDetails(false)}
                                style={{ color: 'red' }}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        <TableContainer component={Paper} elevation={0}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell style={{ fontWeight: 'bold' }}>Missionnaire</TableCell>
                                        <TableCell style={{ fontWeight: 'bold' }}>Dates</TableCell>
                                        <TableCell style={{ fontWeight: 'bold' }}>Objet de la mission</TableCell>
                                        <TableCell style={{ fontWeight: 'bold' }}>Lieu (District/Commune)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(regionDetails?.missions || []).map((m, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell>
                                                <Typography variant="body2" style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                                    {(m.nom || '').toLowerCase()}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="textSecondary">
                                                    {m.directionpers}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="textSecondary">
                                                    {m.service_code}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">
                                                    Du : {m.date_debut}
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                    Au : {m.date_fin}
                                                </Typography>
                                            </TableCell>
                                            <TableCell style={{ maxWidth: '250px', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                                {(m.mission_title || '').toLowerCase()}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {m.nom_district}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {m.lieu_destination}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                </Dialog>
            </Box>
        </MainContainer>
    );
}
